import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type OpenLoginOptions = {
  /** Runs after phone verification (and profile step, if shown) succeeds. May return a Promise (e.g. RSVP); the modal waits before closing. */
  uponVerificationComplete?: () => void | Promise<void>;
};

type LoginModalControllerValue = {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  openLoginModal: (options?: OpenLoginOptions) => void;
  onLoginFlowComplete: () => void;
};

const LoginModalControllerContext = createContext<LoginModalControllerValue | null>(null);

export function LoginModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const pendingCompleteRef = useRef<(() => void | Promise<void>) | undefined>(undefined);

  const setOpen = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      pendingCompleteRef.current = undefined;
    }
  }, []);

  const openLoginModal = useCallback((options?: OpenLoginOptions) => {
    pendingCompleteRef.current = options?.uponVerificationComplete;
    setIsOpen(true);
  }, []);

  const onLoginFlowComplete = useCallback(() => {
    const run = pendingCompleteRef.current;
    pendingCompleteRef.current = undefined;
    const scheduleClose = () => setTimeout(() => setOpen(false), 300);
    try {
      const maybePromise = run?.();
      if (
        maybePromise != null &&
        typeof (maybePromise as Promise<void>).then === "function"
      ) {
        (maybePromise as Promise<void>).finally(scheduleClose);
        return;
      }
    } catch {
      // still close
    }
    scheduleClose();
  }, [setOpen]);

  const value = useMemo(
    () => ({
      isOpen,
      setOpen,
      openLoginModal,
      onLoginFlowComplete,
    }),
    [isOpen, setOpen, openLoginModal, onLoginFlowComplete]
  );

  return (
    <LoginModalControllerContext.Provider value={value}>
      {children}
    </LoginModalControllerContext.Provider>
  );
}

export function useLoginModalController(): LoginModalControllerValue {
  const ctx = useContext(LoginModalControllerContext);
  if (!ctx) {
    throw new Error("useLoginModalController must be used within LoginModalProvider");
  }
  return ctx;
}

export function useOpenLoginModal(): LoginModalControllerValue["openLoginModal"] {
  return useLoginModalController().openLoginModal;
}
